// @ts-nocheck
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useSportsByGender } from '@/hooks/useSportsByGender';
import { usePositionsBySport } from '@/hooks/usePositionsBySport';
import { states } from '@/lib/constants';
import MobileSelect from './MobileSelect';

const athleteSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email'),
  username: z.string().min(3, 'Username must be at least 3 characters'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  phone: z.string().regex(/^\d{3}-\d{3}-\d{4}$/, 'Phone must be in format xxx-xxx-xxxx'),
  city: z.string().min(2, 'City must be at least 2 characters'),
  state: z.string().min(2, 'Please select a state'),
  gender: z.enum(['Men', 'Women'], { required_error: 'Please select NCAA gender' }),
  sport_id: z.string().min(1, 'Please select a sport'),
  position_id: z.string().optional(),
  education_level: z.enum(['high_school', 'college'], { required_error: 'Please select education level' }),
  year: z.string().min(1, 'Please select a year'),
  agreeToTerms: z.boolean().refine(val => val === true, 'You must agree to the terms'),
});

type AthleteFormData = z.infer<typeof athleteSchema>;

interface MobileAthleteFormProps {
  onBack: () => void;
  onSuccess?: () => void;
}

const MobileAthleteForm: React.FC<MobileAthleteFormProps> = ({ onBack, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<AthleteFormData>({
    resolver: zodResolver(athleteSchema),
    defaultValues: {
      agreeToTerms: false
    }
  });

  const selectedGender = watch('gender');
  const selectedSportId = watch('sport_id');
  const selectedEducationLevel = watch('education_level');

  const { sports, isLoading: sportsLoading } = useSportsByGender(selectedGender);
  const { positions, isLoading: positionsLoading } = usePositionsBySport(selectedSportId);

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    if (selectedEducationLevel === 'high_school') {
      const options = [];
      for (let year = currentYear; year <= 2050; year++) {
        options.push({ value: year.toString(), label: year.toString() });
      }
      return options;
    } else if (selectedEducationLevel === 'college') {
      return [
        { value: 'freshman', label: 'Freshman' },
        { value: 'sophomore', label: 'Sophomore' },
        { value: 'junior', label: 'Junior' },
        { value: 'senior', label: 'Senior' },
        { value: 'graduate', label: 'Graduate Student' },
      ];
    }
    return [];
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 6) {
      return digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6, 10);
    } else if (digits.length >= 3) {
      return digits.slice(0, 3) + '-' + digits.slice(3, 6);
    }
    return digits;
  };

  const onSubmit = async (data: AthleteFormData) => {
    setIsLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            name: data.name,
            username: data.username,
            role: 'athlete'
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Failed to create user account');

      const profileData = {
        id: authData.user.id,
        user_id: authData.user.id,
        name: data.name,
        username: data.username,
        email: data.email,
        phone: data.phone,
        city: data.city,
        state: data.state,
        gender: data.gender,
        sport_id: data.sport_id,
        primary_position_id: data.position_id || null,
        education_level: data.education_level,
        college_year: selectedEducationLevel === 'college' ? data.year : null,
        grad_year: selectedEducationLevel === 'high_school' ? parseInt(data.year) : null,
        agree_terms: data.agreeToTerms,
        role: 'athlete' as const,
        primary_role_slug: 'athlete',
        membership_level: 'free',
        active_membership_slug: 'athlete_free',
      };

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(profileData, { onConflict: 'user_id', ignoreDuplicates: false })
        .select()
        .single();

      if (profileError) throw profileError;


      toast.success('Account created successfully!');
      onSuccess?.();
    } catch (error: any) {
      let errorMessage = 'Failed to create account. Please try again.';
      if (error.message?.includes('duplicate key') && error.message?.includes('username')) {
        errorMessage = 'This username is already taken.';
      } else if (error.message?.includes('duplicate key') && error.message?.includes('email')) {
        errorMessage = 'An account with this email already exists.';
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border p-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back</span>
        </button>
        <h1 className="text-xl font-bold mt-2">Create Athlete Account</h1>
      </div>

      {/* Form */}
      <div className="flex-1 p-4 pb-24 overflow-y-auto">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> Parents should register separately and connect to their athlete through the dashboard.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              placeholder="Enter your full name"
              {...register('name')}
              disabled={isLoading}
              className="h-12"
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              {...register('email')}
              disabled={isLoading}
              className="h-12"
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username">Username *</Label>
            <Input
              id="username"
              placeholder="Choose a username"
              {...register('username')}
              disabled={isLoading}
              className="h-12"
            />
            {errors.username && <p className="text-sm text-destructive">{errors.username.message}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone *</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="xxx-xxx-xxxx"
              {...register('phone')}
              disabled={isLoading}
              className="h-12"
              maxLength={12}
              onChange={(e) => {
                const formatted = formatPhone(e.target.value);
                setValue('phone', formatted, { shouldValidate: true });
              }}
            />
            {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a password"
                {...register('password')}
                disabled={isLoading}
                className="h-12 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
          </div>

          {/* City */}
          <div className="space-y-2">
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              placeholder="Enter your city"
              {...register('city')}
              disabled={isLoading}
              className="h-12"
            />
            {errors.city && <p className="text-sm text-destructive">{errors.city.message}</p>}
          </div>

          {/* State */}
          <MobileSelect
            label="State *"
            placeholder="Select your state"
            options={states.map(s => ({ value: s.value, label: s.label }))}
            value={watch('state')}
            onChange={(val) => setValue('state', val, { shouldValidate: true })}
            error={errors.state?.message}
            disabled={isLoading}
          />

          {/* NCAA Gender */}
          <div className="space-y-2">
            <Label>NCAA Gender Classification *</Label>
            <RadioGroup
              onValueChange={(val) => {
                setValue('gender', val as 'Men' | 'Women', { shouldValidate: true });
                setValue('sport_id', '');
                setValue('position_id', '');
              }}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Men" id="men" />
                <Label htmlFor="men" className="font-normal">Men</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Women" id="women" />
                <Label htmlFor="women" className="font-normal">Women</Label>
              </div>
            </RadioGroup>
            {errors.gender && <p className="text-sm text-destructive">{errors.gender.message}</p>}
          </div>

          {/* Sport */}
          <MobileSelect
            label="Sport *"
            placeholder={!selectedGender ? "Select gender first" : sportsLoading ? "Loading..." : "Select your sport"}
            options={sports.map(s => ({ value: s.id, label: s.name }))}
            value={watch('sport_id')}
            onChange={(val) => {
              setValue('sport_id', val, { shouldValidate: true });
              setValue('position_id', '');
            }}
            error={errors.sport_id?.message}
            disabled={isLoading || !selectedGender || sportsLoading}
          />

          {/* Position */}
          {positions.length > 0 && (
            <MobileSelect
              label="Position"
              placeholder={positionsLoading ? "Loading..." : "Select your position"}
              options={positions.map(p => ({ value: p.id, label: p.position_name }))}
              value={watch('position_id') || ''}
              onChange={(val) => setValue('position_id', val)}
              disabled={isLoading || positionsLoading}
            />
          )}

          {/* Education Level */}
          <div className="space-y-2">
            <Label>Education Level *</Label>
            <RadioGroup
              onValueChange={(val) => {
                setValue('education_level', val as 'high_school' | 'college', { shouldValidate: true });
                setValue('year', '');
              }}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="high_school" id="hs" />
                <Label htmlFor="hs" className="font-normal">High School</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="college" id="college" />
                <Label htmlFor="college" className="font-normal">College</Label>
              </div>
            </RadioGroup>
            {errors.education_level && <p className="text-sm text-destructive">{errors.education_level.message}</p>}
          </div>

          {/* Year */}
          {selectedEducationLevel && (
            <MobileSelect
              label={selectedEducationLevel === 'high_school' ? 'Graduation Year *' : 'College Year *'}
              placeholder="Select year"
              options={getYearOptions()}
              value={watch('year')}
              onChange={(val) => setValue('year', val, { shouldValidate: true })}
              error={errors.year?.message}
              disabled={isLoading}
            />
          )}

          {/* Terms */}
          <div className="flex items-start space-x-3">
            <Checkbox
              id="terms"
              checked={watch('agreeToTerms')}
              onCheckedChange={(checked) => setValue('agreeToTerms', checked as boolean, { shouldValidate: true })}
              disabled={isLoading}
            />
            <Label htmlFor="terms" className="text-sm font-normal leading-tight">
              I agree to the Terms of Service and Privacy Policy
            </Label>
          </div>
          {errors.agreeToTerms && <p className="text-sm text-destructive">{errors.agreeToTerms.message}</p>}

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full h-12 bg-secondary hover:bg-secondary/90 text-secondary-foreground font-bold"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Athlete Account'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default MobileAthleteForm;